import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { useTheme } from '../App';
import { apiClient } from '../services/api';
import { supabase } from '../lib/supabase';

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { darkMode } = useTheme();
  const { user, isAuthenticated, changePassword, logout } = useUser();
  const isRTL = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState(() => {
    // Check for hash-based navigation
    const hash = window.location.hash.replace('#', '');
    return ['overview', 'bookmarks', 'security', 'billing'].includes(hash) ? hash : 'overview';
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(true);
  const [freshUserData, setFreshUserData] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [idealizerCheckoutLoading, setIdealizerCheckoutLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch bookmarked ideas from database
  useEffect(() => {
    const fetchBookmarkedIdeas = async () => {
      if (!user) return;
      
      try {
        setLoadingIdeas(true);
        const token = localStorage.getItem('token');
        if (token) {
          const bookmarkedIdeas = await apiClient.getUserBookmarks(token);
          setIdeas(bookmarkedIdeas);
        }
      } catch (err) {
        console.error('Failed to fetch bookmarked ideas:', err);
        setIdeas([]);
      } finally {
        setLoadingIdeas(false);
      }
    };

    fetchBookmarkedIdeas();
  }, [user, i18n.language]); // Re-fetch when user changes or language changes

  // Listen for bookmark updates
  useEffect(() => {
    const handleBookmarksUpdated = (event) => {
      setIdeas(event.detail);
    };

    window.addEventListener('bookmarksUpdated', handleBookmarksUpdated);
    
    return () => {
      window.removeEventListener('bookmarksUpdated', handleBookmarksUpdated);
    };
  }, []);

  // Fetch fresh user data directly from database
  const fetchFreshUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('📡 Fresh user data from database:', userData.user);
        console.log('📡 Fresh subscription_status:', userData.user?.subscription_status);
        console.log('📡 Fresh tier:', userData.user?.tier);
        setFreshUserData(userData.user);
        setRefreshTrigger(prev => prev + 1); // Force component refresh
        console.log('✅ Updated freshUserData state');
      } else {
        console.error('❌ Failed to fetch fresh user data, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching fresh user data:', error);
    }
  };

  // Use user context data and fetch fresh data for subscription info
  useEffect(() => {
    if (user) {
      setFreshUserData({
        credits: user.credits,
        ideaViews: user.ideaViews,
        tier: user.tier,
        bookmarksCount: user.bookmarkedIdeas?.length || 0,
        subscription_status: user.subscription_status || 'inactive'
      });
      setLoadingUserData(false);
      
      // Fetch fresh data for subscription status
      fetchFreshUserData();
      
      console.log('Using user context data:', {
        credits: user.credits,
        ideaViews: user.ideaViews,
        tier: user.tier,
        subscription_status: user.subscription_status
      });
    }
  }, [user]);

  // Debug log to compare values
  useEffect(() => {
    if (user && freshUserData) {
      console.log('User context data:', { 
        credits: user.credits, 
        ideaViews: user.ideaViews, 
        tier: user.tier 
      });
      console.log('Fresh database data:', freshUserData);
    }
  }, [user, freshUserData]);

  // Fetch fresh user data whenever billing tab is accessed
  useEffect(() => {
    if (activeTab === 'billing') {
      console.log('🏦 Billing tab accessed - fetching fresh subscription data...');
      fetchFreshUserData();
    }
  }, [activeTab]);

  // Fetch fresh user data when component mounts
  useEffect(() => {
    console.log('📄 Profile page mounted - fetching fresh user data...');
    fetchFreshUserData();
  }, []);

  // Handle Stripe checkout success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const sessionId = urlParams.get('session_id');
    const tier = urlParams.get('tier');

    if (success && sessionId) {
      // Store session ID for future portal access (only for Pro tier)
      if (tier !== 'idealizer') {
        localStorage.setItem('stripeSessionId', sessionId);
      }
      console.log('💳 Checkout successful, session:', sessionId);
      
      if (tier === 'idealizer') {
        console.log('🌟 Idealizer tier payment successful');
      }
      
      console.log('🔄 Fetching fresh user data after successful checkout...');
      // Fetch fresh data after successful payment
      fetchFreshUserData();
    } else if (canceled) {
      // Show canceled message
      console.log('❌ Checkout was canceled');
    }

    // Also check if user is returning from Stripe portal (no specific URL params but might have updated subscription)
    if (window.location.search.includes('return_url') || document.referrer.includes('billing.stripe.com')) {
      console.log('🏪 User returned from Stripe portal - fetching fresh subscription data...');
      fetchFreshUserData();
    }

    // Clean up URL parameters after handling
    if (success || canceled) {
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Listen for window focus to refresh data when user returns to tab
  useEffect(() => {
    const handleWindowFocus = () => {
      console.log('🔍 Window focused - checking for fresh subscription data...');
      fetchFreshUserData();
    };

    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const bookmarkedIdeas = ideas;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    setCurrentPasswordError('');
    setPasswordMatchError('');
    
    // Validate new passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMatchError('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    // Validate current password is provided
    if (!passwordForm.currentPassword) {
      setCurrentPasswordError('Current password is required');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Auto-hide success message and form after 2 seconds
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      // Handle specific error cases
      const errorMessage = error.message || 'Failed to change password';
      if (errorMessage.includes('current password') || errorMessage.includes('incorrect password')) {
        setCurrentPasswordError('Current password is incorrect');
      } else {
        setPasswordError(errorMessage);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpgradeToPro = async () => {
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const { url } = await apiClient.createCheckoutSession(token);
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setCheckoutLoading(false);
      // You could show an error message to the user here
      alert(error.message || 'Failed to start checkout process');
    }
  };

  const handleUpgradeToIdealizer = async () => {
    setIdealizerCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const { url } = await apiClient.createIdealizerCheckoutSession(token);
      
      // Redirect to Stripe Checkout for one-time payment
      window.location.href = url;
    } catch (error) {
      console.error('Error creating Idealizer checkout session:', error);
      setIdealizerCheckoutLoading(false);
      alert(error.message || 'Failed to start Idealizer checkout process');
    }
  };

  const handleManageSubscription = async () => {
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create portal session directly using customer ID (no checkout session needed)
      const { url } = await apiClient.createPortalSession(token);
      
      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      setCheckoutLoading(false);
      alert(error.message || 'Failed to access subscription management');
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    setShowCancelModal(false);
    setCheckoutLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🚫 Starting subscription cancellation...');
      const result = await apiClient.cancelSubscription(token, true); // Cancel at period end
      
      console.log('✅ Cancellation response:', result);
      
      // Fetch fresh user data to update UI immediately BEFORE showing success message
      await fetchFreshUserData();
      
      // Show success message
      setSuccessMessage(result.message || 'Subscription cancelled successfully!');
      setShowSuccessModal(true);
      setCheckoutLoading(false);
    } catch (error) {
      console.error('❌ Error cancelling subscription:', error);
      setCheckoutLoading(false);
      
      // Show error in success modal (reuse for errors)
      setSuccessMessage(error.message || 'Failed to cancel subscription. Please try again.');
      setShowSuccessModal(true);
    }
  };

  const handleReactivateSubscription = async () => {
    const confirmed = window.confirm(
      'Reactivate your Pro subscription?\n\n' +
      '✅ Your subscription will continue and auto-renew\n' +
      '💳 You\'ll be charged at the end of your current billing period'
    );
    
    if (!confirmed) return;

    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await apiClient.reactivateSubscription(token);
      
      alert(result.message);
      
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      setCheckoutLoading(false);
      alert(error.message || 'Failed to reactivate subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-green-600';
      case 'Moderate':
        return 'text-yellow-600';
      case 'Complex':
        return 'text-red-600';
      default:
        return darkMode ? 'text-gray-300' : 'text-slate-500';
    }
  };

  // Helper function to truncate description
  const truncateDescription = (description, maxLength = 120) => {
    if (!description) return '';
    return description.length > maxLength 
      ? description.substring(0, maxLength) + '...'
      : description;
  };

  // Helper function to get localized content
  const getLocalizedContent = (idea, field) => {
    if (isRTL && idea[`${field}_ar`]) {
      return idea[`${field}_ar`];
    }
    return idea[field] || '';
  };

  // Helper function to translate metric values for bookmarks
  const translateBookmarkMetricValue = (value, type) => {
    if (!value || !isRTL) return value;
    
    const translations = {
      competition: {
        'No competition': 'لا توجد منافسة',
        'Low competition': 'منافسة منخفضة',
        'Medium competition': 'منافسة متوسطة',
        'High competition': 'منافسة عالية',
        'High Competition': 'منافسة عالية'
      },
      risk: {
        'No risk': 'لا توجد مخاطر',
        'Low risk': 'مخاطر منخفضة',
        'Medium risk': 'مخاطر متوسطة',
        'High risk': 'مخاطر عالية'
      }
    };
    
    return translations[type]?.[value] || value;
  };

  // Helper function to translate type and industry for bookmarks
  const translateBookmarkTypeOrIndustry = (value, type) => {
    if (!value || !isRTL) return value;
    
    const translations = {
      type: {
        'SaaS': 'برمجيات الخدمة',
        'Mobile app': 'تطبيق محمول',
        'E-commerce website': 'موقع تجارة إلكترونية',
        'Platform': 'منصة',
        'Web app': 'تطبيق ويب',
        'Website': 'موقع إلكتروني',
        'Marketplace': 'سوق إلكتروني'
      },
      industry: {
        'Education': 'التعليم',
        'Entertainment': 'الترفيه',
        'Sports': 'الرياضة',
        'Shopping': 'التسوق',
        'Social Media': 'وسائل التواصل الاجتماعي',
        'Self-Development': 'التطوير الذاتي',
        'Productivity': 'الإنتاجية',
        'E-commerce': 'التجارة الإلكترونية',
        'Services': 'الخدمات',
        'Fashion': 'الأزياء',
        'Healthcare': 'الرعاية الصحية',
        'Finance': 'المالية',
        'Travel': 'السفر',
        'Food': 'الطعام',
        'Technology': 'التكنولوجيا'
      },
      difficulty: {
        'Easy': 'سهل',
        'Moderate': 'متوسط',
        'Complex': 'معقد'
      }
    };
    
    return translations[type]?.[value] || value;
  };

  return (
    <div className="fade-in-soft">
      {/* Hero Section */}
      <div className="mb-12">
        <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>
          {t('profile.myProfile')}
        </h1>
        <p className={`text-xl mb-8 ${
          darkMode ? 'text-gray-300' : 'text-slate-500'
        } text-start`}>
          {t('profile.manageAccount')}
        </p>
        <div className={`w-24 h-1 bg-gradient-fuchsia rounded-full ${isRTL ? 'ml-0 mr-auto' : 'ml-0 mr-auto'}`}></div>
      </div>

      {/* Profile Tabs */}
      <div className="mb-8">
        <div className={`flex space-x-1 p-1 rounded-lg inline-flex ${
          darkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}>
          {[
            { key: 'overview', label: t('profile.overview') },
            { key: 'bookmarks', label: t('profile.bookmarkedIdeas') },
            { key: 'security', label: t('profile.security') },
            { key: 'billing', label: t('profile.billing') }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === tab.key
                  ? darkMode 
                    ? 'bg-gray-700 text-white' 
                    : 'bg-white text-slate-700 shadow-soft-sm'
                  : darkMode 
                    ? 'text-white hover:text-gray-300' 
                    : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Overview */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* User Information */}
          <div className={`p-6 rounded-2xl shadow-soft-lg ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h2 className={`text-2xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-slate-700'
            } text-start`}>
              {t('profile.accountInformation')}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.name')}
                </label>
                <p className={`text-lg ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {user.name}
                </p>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.email')}
                </label>
                <p className={`text-lg ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {user.email}
                </p>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.dateJoined')}
                </label>
                <p className={`text-lg ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {formatDate(user.dateJoined)}
                </p>
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div className={`p-6 rounded-2xl shadow-soft-lg ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h2 className={`text-2xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-slate-700'
            } text-start`}>
              {t('profile.accountStats')}
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {loadingUserData ? (
                    <div className="animate-pulse bg-gray-300 dark:bg-gray-600 h-8 w-12 rounded"></div>
                  ) : (
                    (freshUserData?.tier || user.tier) === 'pro' || (freshUserData?.tier || user.tier) === 'idealizer' ? '∞' : (freshUserData?.credits ?? user.credits)
                  )}
                </div>
                <div className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  {t('profile.credits')}
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {loadingUserData ? (
                    <div className="animate-pulse bg-gray-300 dark:bg-gray-600 h-8 w-12 rounded"></div>
                  ) : (
                    (freshUserData?.tier || user.tier) === 'pro' || (freshUserData?.tier || user.tier) === 'idealizer' ? '∞' : `${freshUserData?.ideaViews ?? user.ideaViews}/3`
                  )}
                </div>
                <div className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  {t('profile.ideaViews')}
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {loadingUserData ? (
                    <div className="animate-pulse bg-gray-300 dark:bg-gray-600 h-8 w-12 rounded"></div>
                  ) : (
                    freshUserData?.bookmarksCount ?? bookmarkedIdeas.length
                  )}
                </div>
                <div className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  {t('profile.bookmarks')}
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <div className={`text-2xl font-bold ${
                  user.tier === 'pro' ? 'text-yellow-500' : 
                  user.tier === 'idealizer' ? 'text-purple-500' : 'text-blue-500'
                }`}>
                  {user.tier.toUpperCase()}
                </div>
                <div className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  {t('profile.tier')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookmarked Ideas */}
      {activeTab === 'bookmarks' && (
        <div>
          <h2 className={`text-2xl font-bold mb-6 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>
            {t('profile.bookmarkedIdeas')} ({bookmarkedIdeas.length})
          </h2>
          
          {bookmarkedIdeas.length === 0 ? (
            <div className={`text-center py-16 rounded-2xl shadow-soft-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>
                {t('profile.noBookmarkedIdeas')}
              </h3>
              <p className={`mb-6 ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                {t('profile.startExploring')}
              </p>
              <Link to="/ideas-vault" className="btn-soft btn-soft-primary">
                {t('profile.exploreIdeasVault')}
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookmarkedIdeas.map(idea => (
                <Link key={idea.id} to={`/idea/${idea.id}`} className="block hover:no-underline">
                  <div className={`p-6 hover-soft rounded-2xl shadow-soft-lg h-full flex flex-col ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="badge-soft badge-soft-primary">{translateBookmarkTypeOrIndustry(getLocalizedContent(idea, 'type'), 'type')}</div>
                      <div className={`flex items-center space-x-4 text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-500'
                      }`}>
                        <span>{translateBookmarkTypeOrIndustry(getLocalizedContent(idea, 'industry'), 'industry')}</span>
                        <span className={`font-medium ${getDifficultyColor(getLocalizedContent(idea, 'executionDifficulty'))}`}>
                          {translateBookmarkTypeOrIndustry(getLocalizedContent(idea, 'executionDifficulty') || getLocalizedContent(idea, 'execution_difficulty'), 'difficulty')}
                        </span>
                      </div>
                    </div>
                    
                    <h3 className={`text-xl font-bold mb-3 ${
                      darkMode ? 'text-white' : 'text-slate-700'
                    }`}>
                      {getLocalizedContent(idea, 'title')}
                    </h3>
                    <p className={`leading-relaxed mb-4 flex-grow ${
                      darkMode ? 'text-gray-300' : 'text-slate-600'
                    }`}>
                      {truncateDescription(getLocalizedContent(idea, 'description'))}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className={`rounded-lg p-2 text-center ${
                        darkMode ? 'bg-white bg-opacity-20' : 'bg-gray-100'
                      }`}>
                        <div className={`text-xs font-medium ${
                          darkMode ? 'text-white' : 'text-slate-600'
                        }`}>
                          {t('ideasVault.competition')}
                        </div>
                        <div className={`text-xs ${
                          darkMode ? 'text-gray-300' : 'text-slate-500'
                        }`}>
                          {translateBookmarkMetricValue(getLocalizedContent(idea, 'competition'), 'competition')}
                        </div>
                      </div>
                      <div className={`rounded-lg p-2 text-center ${
                        darkMode ? 'bg-white bg-opacity-20' : 'bg-gray-100'
                      }`}>
                        <div className={`text-xs font-medium ${
                          darkMode ? 'text-white' : 'text-slate-600'
                        }`}>
                          {t('ideasVault.risk')}
                        </div>
                        <div className={`text-xs ${
                          darkMode ? 'text-gray-300' : 'text-slate-500'
                        }`}>
                          {translateBookmarkMetricValue(getLocalizedContent(idea, 'regulatoryRisk') || getLocalizedContent(idea, 'regulatory_risk'), 'risk')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-500'
                      }`}>
                        {t('ideasVault.clickToView')}
                      </div>
                      <div className={`flex items-center font-medium ${
                        darkMode ? 'text-white' : 'text-indigo-600'
                      }`}>
                        <span className={isRTL ? 'ml-1' : 'mr-1'}>{t('ideasVault.viewDetails')}</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className={`p-6 rounded-2xl shadow-soft-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h2 className={`text-2xl font-bold mb-6 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>
            {t('profile.securitySettings')}
          </h2>
          
          {!showChangePassword ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`text-lg font-semibold ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>
                    {t('profile.password')}
                  </h3>
                  <p className={`text-sm ${
                    darkMode ? 'text-gray-300' : 'text-slate-500'
                  } text-start`}>
                    {t('profile.changePassword')}
                  </p>
                </div>
                <button 
                  onClick={() => setShowChangePassword(true)}
                  className="btn-soft btn-soft-primary"
                >
                  {t('profile.changePassword')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.currentPassword')}
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => {
                    setPasswordForm({...passwordForm, currentPassword: e.target.value});
                    setCurrentPasswordError(''); // Clear error when user types
                  }}
                  className={`input-soft w-full ${
                    currentPasswordError ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  required
                />
                {currentPasswordError && (
                  <div className="text-red-500 text-sm mt-1">{currentPasswordError}</div>
                )}
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.newPassword')}
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPasswordForm({...passwordForm, newPassword: value});
                    setPasswordError(''); // Clear general error
                    // Clear match error if passwords now match
                    if (passwordForm.confirmPassword && value === passwordForm.confirmPassword) {
                      setPasswordMatchError('');
                    }
                  }}
                  className="input-soft w-full"
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  darkMode ? 'text-gray-300' : 'text-slate-600'
                } text-start`}>
                  {t('profile.confirmNewPassword')}
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPasswordForm({...passwordForm, confirmPassword: value});
                    
                    // Real-time password matching validation
                    if (value && passwordForm.newPassword) {
                      if (value === passwordForm.newPassword) {
                        setPasswordMatchError('');
                      } else {
                        setPasswordMatchError('Passwords do not match');
                      }
                    } else {
                      setPasswordMatchError('');
                    }
                  }}
                  className={`input-soft w-full ${
                    passwordMatchError ? 'border-red-500 focus:border-red-500' : 
                    passwordForm.confirmPassword && passwordForm.newPassword && passwordForm.confirmPassword === passwordForm.newPassword ? 'border-green-500 focus:border-green-500' : ''
                  }`}
                  required
                />
                {passwordMatchError && (
                  <div className="text-red-500 text-sm mt-1">{passwordMatchError}</div>
                )}
                {passwordForm.confirmPassword && passwordForm.newPassword && passwordForm.confirmPassword === passwordForm.newPassword && (
                  <div className="text-green-500 text-sm mt-1 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    Passwords match
                  </div>
                )}
              </div>
              
              {passwordError && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                    </svg>
                    {passwordError}
                  </div>
                </div>
              )}
              
              {passwordSuccess && (
                <div className="text-green-600 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    {passwordSuccess}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={passwordLoading || passwordMatchError || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  className="btn-soft btn-soft-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('profile.changing')}
                    </div>
                  ) : (
                    t('profile.changePassword')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPasswordError('');
                    setPasswordMatchError('');
                  }}
                  className="btn-soft btn-soft-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          <div className={`p-6 rounded-2xl shadow-soft-lg ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h2 className={`text-2xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-slate-700'
            } text-start`}>
              {t('profile.currentPlan')}
            </h2>
            
            <div>
              <h3 className={`text-lg font-semibold ${
                user.tier === 'pro' ? 'text-yellow-500' : 
                user.tier === 'idealizer' ? 'text-purple-500' : 'text-blue-500'
              } text-start`}>
                {user.tier === 'pro' ? t('profile.proAccount') : 
                 user.tier === 'idealizer' ? t('profile.idealizerAccount') : t('profile.freeAccount')}
              </h3>
              <p className={`text-sm ${
                darkMode ? 'text-gray-300' : 'text-slate-500'
              } text-start`}>
                {user.tier === 'pro' ? t('profile.unlimitedAccess') : 
                 user.tier === 'idealizer' ? t('profile.lifetimeAccess') : t('profile.limitedAccess')}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Starter Tier */}
            <div className={`p-6 rounded-2xl shadow-soft-lg border-2 ${
              user.tier === 'free' 
                ? 'border-blue-500' 
                : darkMode ? 'border-gray-700' : 'border-gray-200'
            } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="mb-4">
                <h3 className={`text-xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start mb-2`}>
                  {t('profile.starterAccount')}
                </h3>
                <p className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                } text-start mb-3`}>
                  {t('profile.starterDescription')}
                </p>
                <span className="badge-soft badge-soft-primary">{t('profile.free')}</span>
              </div>
              
              <ul className={`space-y-2 mb-6 ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.fiveCreditsImaginelab')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.threeViewsDetailedIdeas')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.personalDashboard')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeaOfTheDayViews')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeasVaultExploration')}
                </li>
              </ul>
              
              {user.tier === 'free' && (
                <div className="badge-soft badge-soft-success">{t('profile.currentPlan')}</div>
              )}
            </div>

            {/* Pro Tier */}
            <div className={`p-6 rounded-2xl shadow-soft-lg border-2 ${
              user.tier === 'pro' 
                ? 'border-yellow-500' 
                : darkMode ? 'border-gray-700' : 'border-gray-200'
            } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="mb-4">
                <h3 className={`text-xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start mb-2`}>
                  {t('profile.proAccount')}
                </h3>
                <p className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                } text-start mb-3`}>
                  {t('profile.proDescription')}
                </p>
                <span className="badge-soft badge-soft-warning">{t('profile.proPrice')}</span>
              </div>
              
              <ul className={`space-y-2 mb-6 ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedCreditsImaginelab')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedViewsDetailedIdeas')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.personalDashboard')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeaOfTheDayViews')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeasVaultExploration')}
                </li>
              </ul>
              
              {user.tier === 'pro' || user.tier === 'idealizer' ? (
                <div className="space-y-3">
                  <div className="badge-soft badge-soft-warning">
                    {t('profile.currentPlan')}
                  </div>
                  
                  {(() => {
                    if (user.tier === 'idealizer') {
                      return (
                        <div className={`text-center py-3 px-4 rounded-lg ${darkMode ? 'bg-purple-900/20 text-purple-300' : 'bg-purple-50 text-purple-700'}`}>
                          <p className="font-medium">🎉 Lifetime Access Activated</p>
                          <p className="text-sm mt-1">You have permanent access to all Pro features!</p>
                        </div>
                      );
                    }
                    
                    // Pro subscription management logic
                    const subscriptionStatus = freshUserData?.subscription_status || user.subscription_status;
                    const shouldShowOnlyManageButton = subscriptionStatus === 'cancel_at_period_end';
                    
                    if (shouldShowOnlyManageButton) {
                      return (
                        <>
                          <button 
                            onClick={handleManageSubscription}
                            disabled={checkoutLoading}
                            className="w-full px-4 py-3 rounded-lg text-white font-medium bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                          >
                            {checkoutLoading ? (isRTL ? 'جاري التحميل...' : 'Loading...') : (isRTL ? 'إدارة' : 'Manage')}
                          </button>
                          <p className={`text-sm text-center mt-2 ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                            {isRTL ? 'تم إلغاء الاشتراك، اضغط على زر الإدارة لمتابعة رحلتك' : 'Subscription Canceled, Click on Manage button to continue your journey'}
                          </p>
                          <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                            {isRTL ? 'ℹ️ يمكنك إعادة تفعيل الاشتراك من خلال بوابة Stripe' : 'ℹ️ You can reactivate your subscription through the Stripe portal'}
                          </p>
                        </>
                      );
                    }
                    
                    return (
                      <>
                        <div className="flex space-x-2">
                          <button 
                            onClick={handleManageSubscription}
                            disabled={checkoutLoading}
                            className="flex-1 px-4 py-3 rounded-lg text-white font-medium bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                          >
                            {checkoutLoading ? (isRTL ? 'جاري التحميل...' : 'Loading...') : (isRTL ? 'إدارة' : 'Manage')}
                          </button>
                          <button 
                            onClick={handleCancelSubscription}
                            disabled={checkoutLoading}
                            className="flex-1 px-4 py-3 rounded-lg text-white font-medium bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                          >
                            {checkoutLoading ? (isRTL ? 'جاري التحميل...' : 'Loading...') : (isRTL ? 'إلغاء' : 'Cancel')}
                          </button>
                        </div>
                        <p className={`text-xs ${isRTL ? 'text-right' : 'text-left'} ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                          {isRTL ? '💡 الإلغاء يوقف الفواتير المستقبلية ولكن يحتفظ بوصولك للنسخة المدفوعة حتى انتهاء الفترة' : '💡 Cancelling stops future billing but keeps your Pro access until period ends'}
                        </p>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <button 
                  onClick={handleUpgradeToPro}
                  disabled={checkoutLoading}
                  className="btn-soft btn-soft-warning w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    t('profile.upgradeToPro')
                  )}
                </button>
              )}
            </div>

            {/* Idealizer Tier */}
            <div className={`p-6 rounded-2xl shadow-soft-lg border-2 ${
              user.tier === 'idealizer' 
                ? 'border-purple-500' 
                : darkMode ? 'border-gray-700' : 'border-gray-200'
            } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="mb-4">
                <h3 className={`text-xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start mb-2`}>
                  {t('profile.idealizerAccount')}
                </h3>
                <p className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                } text-start mb-3`}>
                  {t('profile.idealizerDescription')}
                </p>
                <span className="px-3 py-1 rounded-full text-white text-sm font-medium bg-gradient-to-r from-purple-600 to-purple-800">{t('profile.lifetimePayment')}</span>
              </div>
              
              <ul className={`space-y-2 mb-6 ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedCreditsImaginelab')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedViewsDetailedIdeas')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.personalDashboard')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeaOfTheDayViews')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  {t('profile.features.unlimitedIdeasVaultExploration')}
                </li>
              </ul>
              
              {user.tier === 'idealizer' ? (
                <div className="space-y-2">
                  <div className="px-4 py-2 rounded-lg text-white text-sm font-medium bg-gradient-to-r from-purple-600 to-purple-800 text-center">{t('profile.currentPlan')}</div>
                  <div className={`text-center text-sm font-medium ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                    🎉 You are an Idealizer, Enjoy Lifetime Access!
                  </div>
                </div>
              ) : (
                <button 
                  onClick={handleUpgradeToIdealizer}
                  disabled={idealizerCheckoutLoading}
                  className="w-full px-4 py-3 rounded-lg text-white font-medium bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {idealizerCheckoutLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    t('profile.upgradeToIdealizer')
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCancelModal(false)}
        >
          <div 
            className={`max-w-md w-full rounded-2xl shadow-soft-xl p-6 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className={`text-xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>
                {isRTL ? 'إلغاء الاشتراك؟' : 'Cancel Subscription?'}
              </h3>
              <div className={`mb-6 ${isRTL ? 'text-right' : 'text-left'} space-y-3 ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                  <span className="text-green-500">✅</span>
                  <span>{isRTL ? 'ستحتفظ بالوصول إلى النسخة المدفوعة حتى نهاية الفترة المدفوعة' : "You'll keep Pro access until the end of your current billing period"}</span>
                </div>
                <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                  <span className="text-red-500">🚫</span>
                  <span>{isRTL ? 'لن تتم أي رسوم مستقبلية' : 'No future charges will occur'}</span>
                </div>
                <div className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                  <span className="text-blue-500">🔄</span>
                  <span>{isRTL ? 'يمكنك إعادة تفعيل الاشتراك في أي وقت قبل انتهاء الفترة' : 'You can reactivate anytime before the period ends'}</span>
                </div>
              </div>
              <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 btn-soft btn-soft-secondary"
                >
                  {isRTL ? 'الاحتفاظ بالاشتراك' : 'Keep Subscription'}
                </button>
                <button
                  onClick={confirmCancelSubscription}
                  disabled={checkoutLoading}
                  className="flex-1 btn-soft btn-soft-danger disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading ? (isRTL ? 'جاري الإلغاء...' : 'Cancelling...') : (isRTL ? 'نعم، إلغاء' : 'Yes, Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Message Modal */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSuccessModal(false)}
        >
          <div 
            className={`max-w-md w-full rounded-2xl shadow-soft-xl p-6 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mb-4">
                <svg className={`w-16 h-16 mx-auto ${
                  successMessage.includes('Failed') || successMessage.includes('error') 
                    ? 'text-red-500' 
                    : 'text-green-500'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {successMessage.includes('Failed') || successMessage.includes('error') ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  )}
                </svg>
              </div>
              <h3 className={`text-xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>
                {successMessage.includes('Failed') || successMessage.includes('error') ? 
                  (isRTL ? 'خطأ' : 'Error') : 
                  (isRTL ? 'نجح!' : 'Success!')
                }
              </h3>
              <p className={`mb-6 leading-relaxed ${isRTL ? 'text-right' : 'text-left'} ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                {successMessage.includes('cancelled successfully') && isRTL ? 
                  'تم إلغاء الاشتراك بنجاح. ستحتفظ بالوصول إلى النسخة المدفوعة حتى نهاية فترة الفوترة.' : 
                  successMessage
                }
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="btn-soft btn-soft-primary w-full"
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beta Modal */}
      {showBetaModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowBetaModal(false)}
        >
          <div 
            className={`max-w-md w-full rounded-2xl shadow-soft-xl p-6 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className={`text-xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>
                Beta Version
              </h3>
              <p className={`mb-6 leading-relaxed ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                {t('profile.betaMessage')}
              </p>
              <button
                onClick={() => setShowBetaModal(false)}
                className="btn-soft btn-soft-primary w-full"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Button */}
      <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={logout}
          className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {t('profile.logout')}
        </button>
      </div>
    </div>
  );
};

export default Profile;